import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  LayoutDashboard,
  Wallet,
  ArrowLeftRight,
  PiggyBank,
  CreditCard,
  History,
  Users,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  Bell,
  Building2,
  Phone,
  Shield,
} from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3f59e367-5274-48ad-9fe7-f7e5ca8c3072/artifacts/mug85kt6_onepay%20logo%20best.jpg";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  { icon: Wallet, label: "Wallet", path: "/wallet" },
  { icon: ArrowLeftRight, label: "Payments", path: "/payments" },
  { icon: Phone, label: "Services", path: "/services" },
  { icon: PiggyBank, label: "Savings", path: "/vaults" },
  { icon: CreditCard, label: "Credit", path: "/credit" },
  { icon: History, label: "Transactions", path: "/transactions" },
  { icon: Users, label: "Recipients", path: "/recipients" },
  { icon: Shield, label: "KYC", path: "/kyc" },
];

// NavContent component extracted outside to avoid re-creation during render
const NavContent = ({ user, location, navItems, setSidebarOpen, mobile = false }) => (
  <div className="flex flex-col h-full">
    {/* Logo */}
    <div className="p-6 border-b border-slate-100">
      <Link to="/dashboard" className="flex items-center gap-2">
        <img src={LOGO_URL} alt="OnePay" className="h-8 w-auto" />
      </Link>
    </div>

    {/* Navigation */}
    <nav className="flex-1 p-4 space-y-1">
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => mobile && setSidebarOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
              isActive
                ? "bg-[#14B8A6] text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </Link>
        );
      })}
    </nav>

    {/* Account Type Badge */}
    {user?.account_type === "sme" && (
      <div className="mx-4 mb-4 p-3 bg-purple-50 rounded-xl">
        <div className="flex items-center gap-2 text-purple-700">
          <Building2 className="h-5 w-5" />
          <span className="text-sm font-medium">SME Account</span>
        </div>
      </div>
    )}

    {/* Settings */}
    <div className="p-4 border-t border-slate-100">
      <Link
        to="/settings"
        onClick={() => mobile && setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
          location.pathname === "/settings"
            ? "bg-[#14B8A6] text-white"
            : "text-slate-600 hover:bg-slate-100"
        }`}
        data-testid="nav-settings"
      >
        <Settings className="h-5 w-5" />
        Settings
      </Link>
    </div>
  </div>
);

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-slate-200">
        <NavContent user={user} location={location} navItems={navItems} setSidebarOpen={setSidebarOpen} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <NavContent user={user} location={location} navItems={navItems} setSidebarOpen={setSidebarOpen} mobile />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </Button>

            {/* Page Title - can be customized */}
            <h1 className="text-lg font-semibold text-slate-900 hidden sm:block">
              {navItems.find((item) => item.path === location.pathname)?.label || "Dashboard"}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="h-5 w-5 text-slate-600" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[#14B8A6] text-white text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium text-slate-700">
                    {user?.name?.split(" ")[0]}
                  </span>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer" data-testid="menu-settings">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
